import { forwardRef } from 'react';
import { LoadingIndicator } from '@THC/components/LoadingIndicator';
import { MDText } from 'i18n-react';

import type { ChatItem } from 'Features/AiCompanion/services';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import LocalTexts from '../../AiCompanion.texts';
import { AgentItem } from '../AgentItem';
import { ErrorItem } from '../ErrorItem';
import { ThinkingSteps } from '../ThinkingSteps';
import { TimeoutItem } from '../TimeoutItem';
import { UserItem } from '../UserItem';

import styles from './ChatItems.module.scss';

const LocalT = new MDText(LocalTexts);

interface ChatItemsProps {
	items: ChatItem[];
	thinking?: boolean;
}

const CHAT_ITEMS = {
	agent: AgentItem,
	error: ErrorItem,
	timeout: TimeoutItem,
	user: UserItem,
} as const;

export const ChatItems = forwardRef<HTMLDivElement, ChatItemsProps>(({ items, thinking }, ref) => {
	const isAiCompanionMultiConversationEnabled = useFeatureToggle(FEATURES.enableAiCompanionMultiConversation);

	const mostRecentUserMessageIndex = items.reduce<number>(
		(acc, item, index) => (item.type === 'user' ? index : acc),
		-1,
	);

	return (
		<>
			{items.map((item, index) => {
				const Item = CHAT_ITEMS[item.type];
				const isMostRecentUserMessage = index === mostRecentUserMessageIndex;

				return isMostRecentUserMessage ? (
					<div key={item.eventId} ref={ref} className={styles.scrollAnchor}>
						<Item item={item} />
					</div>
				) : (
					<Item key={item.eventId} item={item} />
				);
			})}
			{thinking &&
				(isAiCompanionMultiConversationEnabled ? (
					<ThinkingSteps />
				) : (
					<span className={styles.spinner}>
						<LoadingIndicator showText text={LocalT.translate('chatItem.loading') as string} variant="spinner" />
					</span>
				))}
		</>
	);
});

ChatItems.displayName = 'ChatItems';
